// /admin/hr · 人事档案 (HR records). Access: admin + 店长 + 领班 (route-guarded by
// canAccessHR). Tiered visibility: super-admin (Regina) sees restricted manager profiles;
// admin sees all regular + salary; 店长 sees shared-store regular records WITHOUT salary
// (fields omitted entirely server-side); 领班 fully manage their own store+shift records —
// create AND edit, including salary (no paystub/timeline). Mirrors AdminUsers.jsx conventions.
// Backend: /hr/* — see worker/src/routes/hr.ts.

import { useState, useEffect, useMemo, useRef } from 'react';
import * as api from '../lib/api.ts';
import { isElevated, canAccessHR, canSeeHrSalary, isShiftLead } from '../lib/auth.ts';
import { ConfirmDialog } from '../components/ConfirmDialog.jsx';
import { Toast } from '../components/atoms.jsx';
import { confirmDialog } from '../lib/confirm.js';

const STORE_CODES = ['RH', 'DT', 'NY'];
const STATUSES = ['全职', '兼职', '试用', '离职', '休假'];

// True when `name` (trimmed, case-insensitive) already exists among the loaded
// records — drives the create-form same-name guard. Exported for tests.
export function hasNameClash(existingNames, name) {
  const n = (name || '').trim().toLowerCase();
  if (!n) return false;
  return (existingNames || []).some(x => (x || '').trim().toLowerCase() === n);
}
const KIND_LABEL = { progress: '进度', aspiration: '个人意愿', request: '诉求', decision: '公司决策', note: '备注' };
const SALARY_TYPE_LABEL = { monthly: '月薪', hourly: '时薪' };
const DOC_LABEL = { id: '身份证', contract: '合同', paystub: '工资单' };

// Stored hire_date may be YYYY / YYYY-MM / YYYY-MM-DD (legacy partials). The native
// date picker needs a full YYYY-MM-DD, so pad partials for display.
function toDateInput(v) {
  if (!v) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  if (/^\d{4}-\d{2}$/.test(v)) return `${v}-01`;
  if (/^\d{4}$/.test(v)) return `${v}-01-01`;
  return '';
}

function salaryText(r) {
  if (r.salaryAmount == null) return '—';
  const ty = r.salaryType ? SALARY_TYPE_LABEL[r.salaryType] : '';
  return [ty, `${r.salaryAmount}`].filter(Boolean).join(' ');
}

// Initial-circle avatar: first CJK char or first letter, with a deterministic warm tint.
const AVATAR_TINTS = ['#e8d9c4', '#dfe6d6', '#e6dbe6', '#d9e2e8', '#ecdcd2'];
function initials(name) { return (name || '?').trim().charAt(0).toUpperCase(); }
function avatarTint(name) {
  let h = 0; for (let i = 0; i < (name || '').length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_TINTS[h % AVATAR_TINTS.length];
}

// ---------- page ------------------------------------------------------------

export const AdminHR = ({ user }) => {
  const [records, setRecords] = useState(null);     // null = loading
  const [err, setErr] = useState('');
  const [search, setSearch] = useState('');
  const [storeFilter, setStoreFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);  // person shown in the right pane
  const [creating, setCreating] = useState(false);     // right pane shows the new-record form
  const [toast, setToast] = useState(null);
  const [sort, setSort] = useState({ by: 'name', dir: 'asc' });  // default A→Z by name

  const isAdmin = user?.role === 'admin';
  const isSuper = !!user?.superAdmin;
  const canSalary = canSeeHrSalary(user);   // 管理员 or 领班 — sees salary
  const canEdit = isElevated(user);         // 管理员 + 店长 mutate existing records
  const isLead = isShiftLead(user) && !canEdit;  // pure 领班 (a shift, not an elevated role)
  const canCreate = canEdit || isLead;      // 领班 may CREATE (own store+shift) but not edit existing

  const openCreate = () => { setCreating(true); setSelectedId(null); };
  const selectPerson = (id) => { setSelectedId(id); setCreating(false); };
  const closeDetail = () => { setSelectedId(null); setCreating(false); };

  async function refresh() {
    try { setRecords(await api.listHrRecords()); setErr(''); }
    catch (e) { setErr(e?.message || '加载失败'); setRecords([]); }
  }
  useEffect(() => { if (canAccessHR(user)) refresh(); }, []);   // failsafe: no fetch without access

  const filtered = useMemo(() => {
    let r = records || [];
    if (storeFilter !== 'all') r = r.filter(x => x.stores.includes(storeFilter));
    if (statusFilter !== 'all') r = r.filter(x => x.status === statusFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      r = r.filter(x => x.name.toLowerCase().includes(q));
    }
    const dir = sort.dir === 'asc' ? 1 : -1;
    const key = (x) => sort.by === 'hire' ? (x.hireDate || '') : x.name;
    return [...r].sort((a, b) => {
      const ka = key(a), kb = key(b);
      if (ka === kb) return a.name.localeCompare(b.name, 'zh');
      return ka < kb ? -dir : dir;
    });
  }, [records, storeFilter, statusFilter, search, sort]);

  if (!canAccessHR(user)) {
    return <div className="vava-card empty-state"><div>无权访问此页面</div></div>;
  }

  const hasFilter = storeFilter !== 'all' || statusFilter !== 'all' || !!search.trim();
  const selected = creating ? null : (records || []).find(r => r.id === selectedId) || null;
  const detailOpen = creating || !!selected;

  return (
    <>
      <div className="vava-page-header">
        <div>
          <h1 className="vava-page-title">人事档案</h1>
          <div className="vava-page-meta">
            {records === null ? '加载中…' : `${filtered.length} 份档案`}
            {isSuper ? ' · 超级管理员' : isAdmin ? ' · 管理员' : isShiftLead(user) ? ' · 领班' : ' · 店长'}
          </div>
        </div>
        {canCreate && <button className="btn btn-primary" onClick={openCreate}>+ 新建档案</button>}
      </div>

      {err && records !== null && <div className="att-error inv-error" role="alert">{err}</div>}

      <div className={`hr-split ${detailOpen ? 'has-detail' : ''}`}>
        {/* LEFT: people directory */}
        <div className="vava-card hr-list-pane">
          <div className="hr-list-toolbar">
            <div className="inv-search">
              <input type="text" value={search} placeholder="搜姓名…" aria-label="搜索员工"
                     onChange={(e) => setSearch(e.target.value)} />
            </div>
            <div className="hr-filter-row">
              <select className="inv-sort" value={storeFilter} onChange={(e) => setStoreFilter(e.target.value)} aria-label="门店筛选">
                <option value="all">全部门店</option>
                {STORE_CODES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select className="inv-sort" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="状态筛选">
                <option value="all">全部状态</option>
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select className="inv-sort" value={sort.by}
                      onChange={(e) => setSort({ by: e.target.value, dir: e.target.value === 'name' ? 'asc' : 'desc' })} aria-label="排序">
                <option value="name">姓名</option>
                <option value="hire">入职</option>
              </select>
            </div>
          </div>

          {records === null ? (
            <div className="vava-hint hr-list-empty">加载中…</div>
          ) : filtered.length === 0 ? (
            <div className="empty-state hr-empty hr-list-empty">
              {hasFilter ? (
                <>
                  <div className="hr-empty-emoji">🔍</div>
                  <div>没有符合筛选的档案</div>
                  <button className="inv-link-btn" onClick={() => { setSearch(''); setStoreFilter('all'); setStatusFilter('all'); }}>清除筛选</button>
                </>
              ) : (
                <>
                  <div className="hr-empty-emoji">📇</div>
                  <div>{canEdit ? '还没有人事档案' : '你的范围暂无人事档案'}</div>
                  {canEdit && <button className="btn btn-primary" onClick={openCreate}>新建第一份</button>}
                </>
              )}
            </div>
          ) : (
            <ul className="hr-people">
              {filtered.map(r => (
                <li key={r.id}>
                  <button className={`hr-li ${selectedId === r.id ? 'is-active' : ''}`} onClick={() => selectPerson(r.id)}>
                    <span className="hr-avatar" style={{ background: avatarTint(r.name) }}>{initials(r.name)}</span>
                    <span className="hr-li-main">
                      <span className="hr-li-top">
                        <span className="hr-li-name">{r.name}</span>
                        {r.restricted && <span className="hr-restricted-tag">管理层</span>}
                        <span className={`status-badge hr-status-${r.status}`}>{r.status}</span>
                      </span>
                      <span className="hr-li-sub">
                        {r.stores.length > 0 ? r.stores.join('·') : '未分配门店'}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* RIGHT: selected dossier (or create form / placeholder) */}
        <div className="vava-card hr-detail-pane">
          {creating ? (
            <RecordForm
              user={user} isAdmin={isAdmin} isSuper={isSuper} canEdit={canEdit} canCreate={canCreate} isLead={isLead} canSalary={canSalary} record={null}
              existingNames={(records || []).map(r => r.name)}
              onClose={closeDetail} onBack={closeDetail}
              onSaved={(msg) => { closeDetail(); setToast({ kind: 'success', msg }); refresh(); }} />
          ) : selected ? (
            <RecordForm
              key={selected.id}
              user={user} isAdmin={isAdmin} isSuper={isSuper} canEdit={canEdit} canCreate={canCreate} isLead={isLead} canSalary={canSalary} record={selected}
              onClose={closeDetail} onBack={closeDetail} onChanged={refresh}
              onSaved={(msg) => { closeDetail(); setToast({ kind: 'success', msg }); refresh(); }} />
          ) : (
            <div className="empty-state hr-detail-empty">
              <div className="hr-empty-emoji">👈</div>
              <div>选择左侧一位查看档案</div>
              {canCreate && <div className="vava-hint">或点右上角「新建档案」</div>}
            </div>
          )}
        </div>
      </div>

      {toast && <Toast message={toast.msg} kind={toast.kind} onDone={() => setToast(null)} />}
    </>
  );
};

const StorePills = ({ stores }) => {
  const shown = stores.slice(0, 2);
  const extra = stores.length - shown.length;
  return (
    <span className="hr-pills">
      {shown.map(s => <span key={s} className="hr-pill">{s}</span>)}
      {extra > 0 && <span className="hr-pill hr-pill-more">+{extra}</span>}
    </span>
  );
};

// ============================================================================
// Create / edit form — sectioned form + timeline. Rendered inline (expandable
// row for edit, a card for new) rather than in a modal.
// ============================================================================

const RecordForm = ({ user, isAdmin, isSuper, canEdit, canCreate, isLead, canSalary, record, existingNames = [], onClose, onBack, onSaved, onChanged }) => {
  const creating = !record;
  // 领班 fully manage records inside their store+shift scope: create AND edit. Any
  // existing record they can open is already in-scope (server-filtered list), so
  // editing is safe. canSalEdit lets them set wages for their shift's staff too.
  const canMutate = creating ? !!canCreate : (canEdit || !!isLead);
  const readOnly = !canMutate;
  const canSalEdit = isAdmin || !!isLead;      // who may EDIT salary (店长 cannot)
  const [tab, setTab] = useState('overview');  // overview | timeline | files
  // Store-tag options: admin → all; 店长/领班 → only their own stores.
  const storeOptions = isAdmin ? STORE_CODES : (user.stores || []).filter(s => STORE_CODES.includes(s));

  const [name, setName] = useState(record?.name || '');
  const [status, setStatus] = useState(record?.status || '全职');
  const [hireDate, setHireDate] = useState(toDateInput(record?.hireDate));
  // 领班 creates only their own-shift records, so pre-fill + lock the shift to theirs.
  const [shift, setShift] = useState(record?.shift || (!record && isLead ? (user.shift || '') : ''));     // 员工早晚班 tag
  const lockShift = !!isLead;                  // 领班 cannot set/reassign shift (own shift only)
  const [stores, setStores] = useState(record?.stores || []);
  // salary: 管理员 + 领班 (own scope) may edit; 店长 read-only
  const [salaryAmount, setSalaryAmount] = useState(record?.salaryAmount ?? '');
  const [salaryType, setSalaryType] = useState(record?.salaryType || '');
  // super-admin-only
  const [restricted, setRestricted] = useState(!!record?.restricted);

  const [dirty, setDirty] = useState(false);          // create mode only
  const [saving, setSaving] = useState(false);        // create mode only
  const [saveState, setSaveState] = useState('idle'); // edit mode: idle|saving|saved|error
  const [err, setErr] = useState('');
  const [dupConfirm, setDupConfirm] = useState(false); // create mode: same-name guard
  const touch = (setter) => (v) => { setter(v); setDirty(true); setDupConfirm(false); };

  function buildInput() {
    const input = { name: name.trim(), status, hire_date: hireDate || null, shift: shift || null, stores };
    if (canSalEdit) {    // salary writes: 管理员 or 领班 (own scope); server enforces too
      input.salary_amount = salaryAmount === '' ? null : Number(salaryAmount);
      input.salary_type = salaryType || null;
    }
    if (isSuper) input.restricted = restricted;
    return input;
  }

  // Edit mode = auto-save (matches timeline/files, which already save instantly).
  // Debounce field changes; flush on unmount so a quick collapse never loses an edit.
  // 领班 (readOnly) never auto-saves — they can only view.
  const firstRun = useRef(true);
  const pending = useRef(false);
  const latest = useRef(null);
  if (!creating && !readOnly) latest.current = buildInput();

  useEffect(() => {
    if (creating || readOnly) return;
    if (firstRun.current) { firstRun.current = false; return; }
    pending.current = true;
    if (!name.trim()) { setErr('姓名不能为空'); setSaveState('error'); return; }
    setErr('');
    const t = setTimeout(async () => {
      setSaveState('saving');
      try { await api.updateHrRecord(record.id, buildInput()); pending.current = false; setSaveState('saved'); onChanged?.(); }
      catch (e) { setErr(e?.message || '保存失败'); setSaveState('error'); }
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [name, status, hireDate, shift, stores, salaryAmount, salaryType, restricted]);

  useEffect(() => () => {   // unmount flush
    if (!creating && !readOnly && pending.current && latest.current?.name?.trim()) {
      api.updateHrRecord(record.id, latest.current).then(() => onChanged?.()).catch(() => {});
    }
    // eslint-disable-next-line
  }, []);

  const nameClash = hasNameClash(existingNames, name);

  async function create() {
    setErr('');
    if (!name.trim()) { setErr('姓名不能为空'); return; }
    // Same-name guard: first click on a clashing name asks for confirmation
    // (avoids the accidental duplicate dossiers we cleaned up 2026-06-04).
    if (nameClash && !dupConfirm) { setDupConfirm(true); return; }
    setSaving(true);
    try {
      await api.createHrRecord(buildInput());
      onSaved(`已创建 ${name.trim()} 的档案`);
    } catch (e) { setErr(e?.message || '保存失败'); setSaving(false); }
  }

  async function tryClose() {
    if (creating && dirty && !await confirmDialog({ title: '放弃未保存的修改', kind: 'danger', confirmLabel: '放弃并关闭', message: '有未保存的修改，确定关闭？' })) return;
    onClose();
  }

  const toggleStore = (s) => touch(setStores)(stores.includes(s) ? stores.filter(x => x !== s) : [...stores, s]);

  const shiftText = shift === 'morning' ? '早班' : shift === 'evening' ? '晚班' : null;
  const metaBits = [
    stores.length ? stores.join('·') : '未分配门店',
    shiftText,
    hireDate ? `入职 ${hireDate}` : null
  ].filter(Boolean);
  const filesLabel = canSalary ? '薪资与文件' : '文件';

  const overviewSection = (
    <>
      <section className="hr-form-section">
        <h3>基本信息</h3>
        <label className="hr-field"><span>姓名 *</span>
          <input className="vava-input" value={name} disabled={readOnly} onChange={(e) => touch(setName)(e.target.value)} /></label>
        <label className="hr-field"><span>入职时间</span>
          <input className="vava-input" type="date" value={hireDate} disabled={readOnly}
                 onChange={(e) => touch(setHireDate)(e.target.value)} />
        </label>
      </section>

      <section className="hr-form-section">
        <h3>门店与状态</h3>
        <label className="hr-field"><span>状态</span>
          <select className="vava-input" value={status} disabled={readOnly} onChange={(e) => touch(setStatus)(e.target.value)}>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select></label>
        <label className="hr-field"><span>班次（早班/晚班）</span>
          <select className="vava-input" value={shift} disabled={readOnly || lockShift} onChange={(e) => touch(setShift)(e.target.value)}>
            <option value="">不设</option>
            <option value="morning">早班</option>
            <option value="evening">晚班</option>
          </select>
          <div className="vava-hint">
            {lockShift ? '领班只能为本班次员工建档（班次已锁定）。' : '用于领班权限：领班只能看到本店且同班次员工。'}
          </div>
        </label>
        <div className="hr-field"><span>门店 {!isAdmin && '(仅本人门店)'}</span>
          <div className="hr-checkgrid">
            {storeOptions.map(s => (
              <label key={s} className={`hr-check ${stores.includes(s) ? 'on' : ''}`}>
                <input type="checkbox" checked={stores.includes(s)} onChange={() => toggleStore(s)}
                       disabled={readOnly || (!isAdmin && !creating)} /> {s}
              </label>
            ))}
          </div>
          <div className="vava-hint">
            {creating && isLead ? '领班须选择本人门店，否则建档后自己将看不到。' : '不属于单一门店可不选；未选门店的档案店长看不到。'}
          </div>
        </div>
        {isSuper && (
          <label className="hr-check-inline">
            <input type="checkbox" checked={restricted} disabled={readOnly} onChange={(e) => touch(setRestricted)(e.target.checked)} />
            管理层档案（仅超级管理员可见）
          </label>
        )}
      </section>
    </>
  );

  const filesSection = (
    <section className="hr-form-section">
      {canSalary && (
        <label className="hr-field"><span>薪资 {canSalEdit ? '' : '（只读）'}</span>
          {canSalEdit ? (
            <span className="hr-inline">
              <select className="vava-input" value={salaryType} disabled={readOnly} onChange={(e) => touch(setSalaryType)(e.target.value)}>
                <option value="">类型</option>
                <option value="monthly">月薪</option>
                <option value="hourly">时薪</option>
              </select>
              <input className="vava-input" type="number" placeholder="金额（可空）" value={salaryAmount} disabled={readOnly}
                     onChange={(e) => touch(setSalaryAmount)(e.target.value)} />
            </span>
          ) : (
            <div className="hr-readonly">{salaryText({ salaryAmount: record?.salaryAmount, salaryType: record?.salaryType })}</div>
          )}
        </label>
      )}
      <DocPanel recordId={record?.id} kind="id" readOnly={readOnly} />
      <DocPanel recordId={record?.id} kind="contract" readOnly={readOnly} />
      {canSalary && isAdmin && <DocPanel recordId={record?.id} kind="paystub" readOnly={readOnly} />}
    </section>
  );

  return (
    <div className="hr-form hr-detail">
      <div className="hr-detail-head">
        {onBack && <button className="hr-back" onClick={() => (creating ? tryClose() : onBack())}>‹ 返回</button>}
        <span className="hr-avatar hr-avatar-lg" style={{ background: avatarTint(name || '?') }}>{initials(name || '?')}</span>
        <div className="hr-detail-id">
          <div className="hr-detail-name">
            {creating ? '新建人事档案' : (name || '（未命名）')}
            {!creating && <span className={`status-badge hr-status-${status}`}>{status}</span>}
            {restricted && <span className="hr-restricted-tag">管理层</span>}
          </div>
          {!creating && <div className="hr-detail-meta">{metaBits.join(' · ')}</div>}
        </div>
        {!creating && isAdmin && <DeleteButton record={record} onDeleted={() => onSaved(`已归档 ${name}`)} />}
      </div>

      {err && <div className="att-error" role="alert">{err}</div>}

      {creating ? (
        <>
          {overviewSection}
          <div className="vava-hint">保存后可添加 跟进记录 和上传 文件。</div>
        </>
      ) : (
        <>
          <div className="hr-tabs">
            <button className={`hr-tab ${tab === 'overview' ? 'on' : ''}`} onClick={() => setTab('overview')}>概览</button>
            <button className={`hr-tab ${tab === 'timeline' ? 'on' : ''}`} onClick={() => setTab('timeline')}>跟进记录</button>
            <button className={`hr-tab ${tab === 'files' ? 'on' : ''}`} onClick={() => setTab('files')}>{filesLabel}</button>
          </div>
          {tab === 'overview' && overviewSection}
          {tab === 'timeline' && (
            <section className="hr-form-section">
              <Timeline recordId={record.id} isAdmin={isAdmin} readOnly={readOnly} />
            </section>
          )}
          {tab === 'files' && filesSection}
        </>
      )}

      {creating && dupConfirm && (
        <div className="att-warning" role="alert" style={{ marginTop: 8 }}>
          已有同名档案「<strong>{name.trim()}</strong>」· 确认仍要新建一份？再点一次「确认新建」即可。
        </div>
      )}
      <div className="hr-form-actions">
        {creating ? (
          <>
            <button className="btn btn-secondary" onClick={tryClose} disabled={saving}>取消</button>
            <button className={`btn ${dupConfirm ? 'btn-secondary' : 'btn-primary'}`} onClick={create} disabled={saving}>
              {saving ? '创建中…' : dupConfirm ? '确认新建' : '创建档案'}
            </button>
          </>
        ) : readOnly ? (
          <span className="hr-savestate">只读（领班）</span>
        ) : (
          <span className={`hr-savestate s-${saveState}`}>
            {saveState === 'saving' ? '保存中…'
              : saveState === 'saved' ? '已自动保存 ✓'
              : saveState === 'error' ? '保存失败'
              : '改动会自动保存'}
          </span>
        )}
      </div>
    </div>
  );
};


const DeleteButton = ({ record, onDeleted }) => {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  return (
    <>
      <button className="btn btn-danger" onClick={() => setConfirm(true)} style={{ marginRight: 'auto' }}>归档</button>
      {confirm && (
        <ConfirmDialog
          title="归档档案" kind="danger" confirmLabel={busy ? '处理中…' : '确认归档'}
          message={`归档 ${record.name} 的人事档案？将从列表隐藏（软删除，可由超级管理员恢复）。`}
          onConfirm={async () => { setBusy(true); try { await api.deleteHrRecord(record.id); onDeleted(); } finally { setBusy(false); } }}
          onClose={() => { if (!busy) setConfirm(false); }} />
      )}
    </>
  );
};

// ---------- timeline (进度 / 诉求 / 公司决策 / 备注) -------------------------
// Unified dated log. Adding appends; deleting soft-deletes (backend keeps the row
// + who/when as the audit log Regina asked for).

const Timeline = ({ recordId, isAdmin, readOnly }) => {
  const [entries, setEntries] = useState(null);
  const [kind, setKind] = useState('progress');
  const [content, setContent] = useState('');
  const [adminOnly, setAdminOnly] = useState(false);
  const [busy, setBusy] = useState(false);
  const [delId, setDelId] = useState(null);
  const [filterKind, setFilterKind] = useState('all');

  async function load() { try { setEntries(await api.listHrTimeline(recordId)); } catch { setEntries([]); } }
  useEffect(() => { load(); /* eslint-disable-line */ }, [recordId]);

  async function add() {
    if (!content.trim()) return;
    setBusy(true);
    try { await api.addHrTimeline(recordId, kind, content.trim(), isAdmin && adminOnly); setContent(''); setAdminOnly(false); await load(); }
    finally { setBusy(false); }
  }

  async function remove(id) {
    setBusy(true);
    try { await api.deleteHrTimeline(recordId, id); await load(); }
    finally { setBusy(false); setDelId(null); }
  }

  const shown = (entries || []).filter(e => filterKind === 'all' || e.kind === filterKind);
  const showFilter = entries && entries.length > 6;

  return (
    <div className="hr-timeline">
      {!readOnly && (
        <div className="hr-timeline-add">
          <select className="vava-input hr-kind-select" value={kind} onChange={(e) => setKind(e.target.value)}>
            {Object.entries(KIND_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <textarea className="vava-input hr-timeline-text" rows={2} placeholder="添加一条…（可多行，Ctrl+Enter 提交）"
                    value={content} onChange={(e) => setContent(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) add(); }} />
          <button className="btn btn-secondary" onClick={add} disabled={busy || !content.trim()}>添加</button>
        </div>
      )}
      {!readOnly && isAdmin && (
        <label className="hr-check-inline">
          <input type="checkbox" checked={adminOnly} onChange={(e) => setAdminOnly(e.target.checked)} /> 仅管理员可见
        </label>
      )}
      {showFilter && (
        <div className="hr-feed-filter">
          <button className={`hr-chip ${filterKind === 'all' ? 'on' : ''}`} onClick={() => setFilterKind('all')}>全部</button>
          {Object.entries(KIND_LABEL).map(([k, v]) => (
            <button key={k} className={`hr-chip ${filterKind === k ? 'on' : ''}`} onClick={() => setFilterKind(k)}>{v}</button>
          ))}
        </div>
      )}
      {entries === null ? <div className="vava-hint">加载中…</div>
        : entries.length === 0 ? <div className="vava-hint">暂无记录</div>
        : shown.length === 0 ? <div className="vava-hint">没有「{KIND_LABEL[filterKind]}」记录</div>
        : (
          <ul className="hr-feed">
            {shown.map(e => (
              <li key={e.id} className={`hr-feed-item k-${e.kind}`}>
                <div className="hr-feed-head">
                  <span className={`hr-kind-tag k-${e.kind}`}>{KIND_LABEL[e.kind]}</span>
                  {e.adminOnly && <span className="hr-admin-tag">仅管理员</span>}
                  <span className="hr-feed-meta">{e.actorName || '系统'} · {fmtTime(e.createdAt)}</span>
                  {!readOnly && <button className="hr-feed-del" title="删除" disabled={busy} onClick={() => setDelId(e.id)}>×</button>}
                </div>
                <div className="hr-feed-content">{e.content}</div>
              </li>
            ))}
          </ul>
        )}
      {delId != null && (
        <ConfirmDialog
          title="删除记录" kind="danger" confirmLabel={busy ? '删除中…' : '确认删除'}
          message="删除这条跟进记录？（后台会保留删除日志）"
          onConfirm={() => remove(delId)}
          onClose={() => { if (!busy) setDelId(null); }} />
      )}
    </div>
  );
};

// ---------- documents (real file upload/download per kind) ------------------

const DocPanel = ({ recordId, kind, readOnly }) => {
  const [docs, setDocs] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [delId, setDelId] = useState(null);

  async function load() {
    try { const all = await api.listHrDocuments(recordId); setDocs(all.filter(d => d.kind === kind)); }
    catch { setDocs([]); }
  }
  useEffect(() => { load(); /* eslint-disable-line */ }, [recordId]);

  async function onPick(e) {
    const file = e.target.files?.[0];
    e.target.value = '';                         // allow re-picking the same file
    if (!file) return;
    setBusy(true); setErr('');
    try { await api.uploadHrDocument(recordId, kind, file); await load(); }
    catch (ex) { setErr(ex?.message || '上传失败'); }
    finally { setBusy(false); }
  }

  async function remove(id) {
    setBusy(true);
    try { await api.deleteHrDocument(id); await load(); }
    finally { setBusy(false); setDelId(null); }
  }

  return (
    <div className="hr-field hr-docs">
      <span>{DOC_LABEL[kind]}文件</span>
      <div className="hr-docs-body">
        {docs === null ? <div className="vava-hint">加载中…</div>
          : docs.length === 0 ? <div className="vava-hint">暂无文件</div>
          : (
            <ul className="hr-doc-list">
              {docs.map(d => (
                <li key={d.id} className="hr-doc-item">
                  <button className="hr-doc-name" onClick={() => api.downloadHrDocument(d.id, d.filename)}>
                    ⬇ {d.filename}
                  </button>
                  <span className="hr-doc-meta">{(d.sizeBytes / 1024).toFixed(0)}KB</span>
                  {!readOnly && <button className="hr-feed-del" title="删除" disabled={busy} onClick={() => setDelId(d.id)}>×</button>}
                </li>
              ))}
            </ul>
          )}
        {!readOnly && (
          <label className={`btn btn-secondary hr-upload-btn ${busy ? 'is-busy' : ''}`}>
            {busy ? '上传中…' : `上传${DOC_LABEL[kind]}`}
            <input type="file" hidden disabled={busy} onChange={onPick} />
          </label>
        )}
        {err && <div className="att-error" style={{ marginTop: 6 }}>{err}</div>}
      </div>
      {delId != null && (
        <ConfirmDialog
          title="删除文件" kind="danger" confirmLabel={busy ? '删除中…' : '确认删除'}
          message="删除这个文件？（后台会保留删除日志）"
          onConfirm={() => remove(delId)}
          onClose={() => { if (!busy) setDelId(null); }} />
      )}
    </div>
  );
};

function fmtTime(iso) {
  if (!iso) return '';
  const d = new Date(iso.replace(' ', 'T') + (iso.endsWith('Z') ? '' : 'Z'));
  if (isNaN(d.getTime())) return iso;
  return `${d.getMonth() + 1}月${d.getDate()}日`;
}
